import { useState, useEffect } from 'react';
import { Store, Warehouse, ShoppingCart, TrendingUp, Euro, RefreshCw, Truck, CalendarDays, BookmarkCheck, Package, Activity, RotateCcw, Wallet, ChevronDown, ChevronUp, Calendar, Receipt } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { getPriceWithoutTVA, getTVAAmount } from '../lib/utils';

// Format price with comma as decimal separator
const formatPrice = (value) => {
    if (value === undefined || value === null) return '0,00';
    return value.toFixed(2).replace('.', ',');
};

// Check if V3 (TVA mode) is active
const isV3 = () => localStorage.getItem('app_version') === 'v3';

export const AcasaV2 = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [showTransportModal, setShowTransportModal] = useState(false);
    const [transportHistory, setTransportHistory] = useState(null);
    const [expandedMonth, setExpandedMonth] = useState(null);
    const [activityCounter, setActivityCounter] = useState(null);
    const [resettingCounter, setResettingCounter] = useState(false);
    const [showSalesModal, setShowSalesModal] = useState(false);
    const [salesHistory, setSalesHistory] = useState(null);
    const [expandedSalesMonth, setExpandedSalesMonth] = useState(null);
    const [tvaSummary, setTvaSummary] = useState(null);

    useEffect(() => {
        loadDashboard();
        loadActivityCounter();
        loadTvaSummary();
    }, []);

    const loadTvaSummary = async () => {
        try {
            const res = await api.get('/expenses/v2/tva-summary');
            setTvaSummary(res.data);
        } catch (err) {
            console.error('TVA summary error:', err);
        }
    };

    const loadActivityCounter = async () => {
        try {
            const res = await api.get('/activity-counter/v2');
            setActivityCounter(res.data);
        } catch (err) {
            console.error('Activity counter error:', err);
        }
    };

    const resetActivityCounter = async () => {
        setResettingCounter(true);
        try {
            await api.post('/activity-counter/v2/reset');
            await loadActivityCounter();
            toast.success('Contor resetat cu succes');
        } catch (err) {
            toast.error('Eroare la resetarea contorului');
        }
        setResettingCounter(false);
    };

    const loadDashboard = async () => {
        setLoading(true);
        try {
            const res = await api.get('/v2/dashboard');
            setData(res.data);
        } catch (err) {
            console.error('Dashboard error:', err);
            console.error('Response:', err.response?.data);
            console.error('Status:', err.response?.status);
            toast.error(`Eroare la încărcarea dashboard-ului: ${err.response?.status || err.message}`);
        }
        setLoading(false);
    };

    const loadTransportHistory = async () => {
        try {
            const res = await api.get('/v2/transport-fees');
            setTransportHistory(res.data);
            setShowTransportModal(true);
        } catch (err) {
            toast.error('Eroare la încărcarea istoricului transport');
        }
    };

    const loadSalesHistory = async () => {
        try {
            const res = await api.get('/v2/sales-history');
            setSalesHistory(res.data);
            setShowSalesModal(true);
        } catch (err) {
            toast.error('Eroare la încărcarea istoricului vânzări');
        }
    };

    const getMonthName = (month) => {
        const months = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 
                       'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
        return months[month - 1] || '';
    };

    const ValueCard = ({ title, icon: Icon, valueRon, valueEur, count, color, onClick }) => (
        <div 
            className={`bg-white border border-zinc-200 rounded-xl p-4 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
            onClick={onClick}
        >
            <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
                    <Icon size={20} className="text-white" />
                </div>
                {count !== undefined && (
                    <span className="text-xs text-zinc-400 bg-zinc-100 px-2 py-1 rounded-full">
                        {count} {count === 1 ? 'articol' : 'articole'}
                    </span>
                )}
            </div>
            <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">{title}</p>
            <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {formatPrice(valueRon)}
                </span>
                <span className="text-sm text-zinc-400">RON</span>
            </div>
            <div className="flex items-center gap-1 mt-1">
                <Euro size={12} className="text-blue-500" />
                <span className="text-sm text-blue-600 font-medium">{formatPrice(valueEur)} EUR</span>
            </div>
        </div>
    );

    return (
        <div className="pb-24" data-testid="acasa-v2-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Dashboard
                    </h1>
                    <p className="text-zinc-500">Toth Commerce PFA - Rezumat</p>
                </div>
                <button
                    onClick={loadDashboard}
                    className="p-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 transition-colors"
                    title="Reîncarcă"
                >
                    <RefreshCw size={20} className={`text-zinc-600 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {loading ? (
                <div className="grid grid-cols-2 gap-4">
                    <Skeleton className="h-36 rounded-xl" />
                    <Skeleton className="h-36 rounded-xl" />
                    <Skeleton className="h-36 rounded-xl" />
                    <Skeleton className="h-36 rounded-xl" />
                </div>
            ) : data ? (
                <>
                    {/* Value Cards Grid */}
                    <div className="grid grid-cols-2 gap-4">
                        <ValueCard
                            title="Comenzi Active"
                            icon={ShoppingCart}
                            valueRon={data.orders?.value_ron || 0}
                            valueEur={data.orders?.value_eur || 0}
                            count={data.orders?.count}
                            color="bg-amber-500"
                            onClick={() => navigate('/comenzi-v2')}
                        />
                        <ValueCard
                            title="Vânzări Totale"
                            icon={TrendingUp}
                            valueRon={(data.sales?.with_initial_ron || 0) + (data.virtual_orders?.completed_total_ron || 0)}
                            valueEur={((data.sales?.with_initial_ron || 0) + (data.virtual_orders?.completed_total_ron || 0)) / (data.euro_rate || 5)}
                            count={(data.sales?.count || 0) + (data.virtual_orders?.completed_count || 0)}
                            color="bg-violet-500"
                            onClick={() => navigate('/facturi')}
                        />
                        <ValueCard
                            title="Valoare Magazin (Est. Vânzare)"
                            icon={Store}
                            valueRon={data.store?.value_ron || 0}
                            valueEur={data.store?.value_eur || 0}
                            count={data.store?.count}
                            color="bg-emerald-500"
                            onClick={() => navigate('/magazin-v2')}
                        />
                        <ValueCard
                            title="Valoare Depozit"
                            icon={Warehouse}
                            valueRon={data.depot?.value_ron || 0}
                            valueEur={data.depot?.value_eur || 0}
                            count={data.depot?.count}
                            color="bg-indigo-500"
                            onClick={() => navigate('/depozit-v2')}
                        />
                    </div>

                    {/* Profit Cards - 4 columns */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                                    <TrendingUp size={16} className="text-white" />
                                </div>
                                <span className="text-xs text-emerald-600 font-medium uppercase tracking-wide">Profit Estimat</span>
                            </div>
                            <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatPrice(data.profit?.estimated_ron || 0)} <span className="text-sm font-normal">RON</span>
                            </p>
                            <p className="text-xs text-emerald-600 mt-1">
                                Vânzări + Comenzi - Cheltuieli
                            </p>
                        </div>
                        <div className="bg-gradient-to-br from-violet-50 to-violet-100 border border-violet-200 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
                                    <TrendingUp size={16} className="text-white" />
                                </div>
                                <span className="text-xs text-violet-600 font-medium uppercase tracking-wide">Profit Real</span>
                            </div>
                            <p className="text-2xl font-bold text-violet-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatPrice(data.profit?.real_ron || 0)} <span className="text-sm font-normal">RON</span>
                            </p>
                            <p className="text-xs text-violet-600 mt-1">
                                Vânzări efective - Cheltuieli
                            </p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                                    <ShoppingCart size={16} className="text-white" />
                                </div>
                                <span className="text-xs text-blue-600 font-medium uppercase tracking-wide">Vânzări Azi</span>
                            </div>
                            <p className="text-2xl font-bold text-blue-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatPrice(data.today_sales?.value_ron || 0)} <span className="text-sm font-normal">RON</span>
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                                {data.today_sales?.count || 0} articole vândute azi
                            </p>
                        </div>
                        <div 
                            className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow"
                            onClick={() => navigate('/comenzi-v2')}
                            data-testid="today-orders-card"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center">
                                    <CalendarDays size={16} className="text-white" />
                                </div>
                                <span className="text-xs text-orange-600 font-medium uppercase tracking-wide">Comenzi Azi</span>
                            </div>
                            <p className="text-2xl font-bold text-orange-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatPrice(data.today_orders?.value_ron || 0)} <span className="text-sm font-normal">RON</span>
                            </p>
                            <p className="text-xs text-orange-600 mt-1">
                                {data.today_orders?.count || 0} clienți ({data.today_orders?.items_count || 0} articole)
                            </p>
                        </div>

                        {/* Today Reservations */}
                        <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-7 h-7 rounded-lg bg-teal-500 flex items-center justify-center">
                                    <CalendarDays size={16} className="text-white" />
                                </div>
                                <span className="text-xs text-teal-600 font-medium uppercase tracking-wide">Rezervări Azi</span>
                            </div>
                            <p className="text-2xl font-bold text-teal-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatPrice(data.today_reservations?.value_ron || 0)} <span className="text-sm font-normal">RON</span>
                            </p>
                            <p className="text-xs text-teal-600 mt-1">
                                {data.today_reservations?.count || 0} clienți ({data.today_reservations?.qty || 0} produse)
                            </p>
                        </div>

                        {/* Virtual Orders */}
                        <div 
                            className="bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow"
                            onClick={() => navigate('/comenzi-virtuale')}
                            data-testid="virtual-orders-card"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-7 h-7 rounded-lg bg-cyan-500 flex items-center justify-center">
                                    <Package size={16} className="text-white" />
                                </div>
                                <span className="text-xs text-cyan-600 font-medium uppercase tracking-wide">Comenzi Neconfirmate</span>
                            </div>
                            <p className="text-2xl font-bold text-cyan-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatPrice(data.virtual_orders?.value_ron || 0)} <span className="text-sm font-normal">RON</span>
                            </p>
                            <p className="text-xs text-cyan-600 mt-1">
                                {data.virtual_orders?.count || 0} comenzi ({data.virtual_orders?.items_count || 0} produse)
                            </p>
                        </div>

                        {/* Cheltuieli Totale */}
                        <div 
                            className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow"
                            onClick={() => navigate('/rapoarte')}
                            data-testid="expenses-card"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-7 h-7 rounded-lg bg-red-500 flex items-center justify-center">
                                    <Wallet size={16} className="text-white" />
                                </div>
                                <span className="text-xs text-red-600 font-medium uppercase tracking-wide">Cheltuieli Totale</span>
                            </div>
                            <p className="text-2xl font-bold text-red-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatPrice(data.expenses?.with_initial_ron || data.profit?.expenses_with_initial || 0)} <span className="text-sm font-normal">RON</span>
                            </p>
                            <p className="text-xs text-red-600 mt-1">
                                Luna curentă: {formatPrice(data.expenses?.month_ron || 0)} RON
                            </p>
                        </div>
                    </div>

                    {/* Reservations Card */}
                    {(data.reservations?.total_qty > 0) && (
                        <div 
                            className="mt-4 bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow"
                            onClick={() => navigate('/rezervari')}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center">
                                        <BookmarkCheck size={20} className="text-white" />
                                    </div>
                                    <div>
                                        <span className="text-sm text-teal-600 font-medium uppercase tracking-wide">Produse Rezervate</span>
                                        <p className="text-2xl font-bold text-teal-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                            {data.reservations?.total_qty || 0} <span className="text-sm font-normal">produse</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-xl font-bold text-teal-700">
                                        {formatPrice(data.reservations?.total_value_ron || 0)} RON
                                    </p>
                                    <p className="text-xs text-teal-500">
                                        {data.reservations?.unique_customers || 0} clienți
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TVA Summary Cards - Only in V3 mode */}
                    {isV3() && (
                        <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                                    <Receipt size={16} className="text-white" />
                                </div>
                                <h3 className="font-semibold text-blue-800">Defalcare TVA (21%)</h3>
                                <span className="text-xs text-blue-500 ml-auto">Din August 2025</span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {/* Vânzări Azi - TVA */}
                                <div className="bg-white/70 rounded-lg p-3 border border-blue-100">
                                    <p className="text-xs text-zinc-500 mb-1">Vânzări Azi</p>
                                    <p className="text-sm font-medium text-zinc-700">
                                        Bază: {formatPrice(getPriceWithoutTVA(data.today_sales?.value_ron || 0))}
                                    </p>
                                    <p className="text-sm font-bold text-blue-600">
                                        TVA: {formatPrice(getTVAAmount(data.today_sales?.value_ron || 0))}
                                    </p>
                                </div>
                                {/* Comenzi Active - TVA */}
                                <div className="bg-white/70 rounded-lg p-3 border border-blue-100">
                                    <p className="text-xs text-zinc-500 mb-1">Comenzi Active</p>
                                    <p className="text-sm font-medium text-zinc-700">
                                        Bază: {formatPrice(getPriceWithoutTVA(data.orders?.value_ron || 0))}
                                    </p>
                                    <p className="text-sm font-bold text-blue-600">
                                        TVA: {formatPrice(getTVAAmount(data.orders?.value_ron || 0))}
                                    </p>
                                </div>
                                {/* Rezervări - TVA */}
                                <div className="bg-white/70 rounded-lg p-3 border border-blue-100">
                                    <p className="text-xs text-zinc-500 mb-1">Rezervări</p>
                                    <p className="text-sm font-medium text-zinc-700">
                                        Bază: {formatPrice(getPriceWithoutTVA(data.reservations?.total_value_ron || 0))}
                                    </p>
                                    <p className="text-sm font-bold text-blue-600">
                                        TVA: {formatPrice(getTVAAmount(data.reservations?.total_value_ron || 0))}
                                    </p>
                                </div>
                                {/* Luna Curentă - TVA */}
                                <div className="bg-white/70 rounded-lg p-3 border border-blue-100">
                                    <p className="text-xs text-zinc-500 mb-1">Vânzări Luna</p>
                                    <p className="text-sm font-medium text-zinc-700">
                                        Bază: {formatPrice(getPriceWithoutTVA(data.month_sales?.value_ron || 0))}
                                    </p>
                                    <p className="text-sm font-bold text-blue-600">
                                        TVA: {formatPrice(getTVAAmount(data.month_sales?.value_ron || 0))}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Quick Stats Summary */}
                    <div className="mt-6 p-4 bg-zinc-50 border border-zinc-200 rounded-xl">
                        <h3 className="font-semibold text-zinc-700 mb-3">Rezumat Total (cu Schimbare An)</h3>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-600">Val. Comenzi Active</span>
                                <span className="font-bold text-amber-600">
                                    {formatPrice(data.orders?.value_ron || 0)} RON
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-600">Val. Comenzi Finalizate (Total)</span>
                                <span className="font-bold text-emerald-600">
                                    {formatPrice((data.sales?.with_initial_ron || 0) + (data.virtual_orders?.completed_total_ron || 0))} RON
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <span className="text-zinc-600">Vânzări Luna Curentă (incl. Virtuale)</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="font-bold text-violet-600">
                                        {formatPrice(data.month_sales?.value_ron || 0)} RON
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={loadSalesHistory}
                                        className="text-xs text-blue-600 hover:text-blue-700 h-6 px-2"
                                    >
                                        Vezi istoric
                                    </Button>
                                </div>
                            </div>
                            <div className="border-t border-zinc-200 pt-2 mt-2">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-zinc-600">Cheltuieli Totale (cu Schimbare An)</span>
                                    <span className="font-bold text-red-600">
                                        {formatPrice(data.profit?.expenses_with_initial || 0)} RON
                                    </span>
                                </div>
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-zinc-600">Profit Estimat (Vânzări + Comenzi + Rezervări - Cheltuieli)</span>
                                    <span className={`font-bold ${(data.profit?.estimated_ron || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {formatPrice(data.profit?.estimated_ron || 0)} RON
                                    </span>
                                </div>
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-zinc-600 font-medium">Profit Real (Vânzări efective - Cheltuieli)</span>
                                    <span className={`font-bold ${(data.profit?.real_ron || 0) >= 0 ? 'text-violet-600' : 'text-red-600'}`}>
                                        {formatPrice(data.profit?.real_ron || 0)} RON
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-2 bg-amber-50 rounded-lg mt-2">
                                    <span className="text-amber-800 font-medium">Profit Propriu-zis (Vânzare - Cost)</span>
                                    <span className={`font-bold text-lg ${(data.profit?.propriu_ron || 0) >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                                        {formatPrice(data.profit?.propriu_ron || 0)} RON
                                    </span>
                                </div>
                            </div>
                            <div className="border-t border-zinc-200 pt-2 mt-2 flex justify-between items-center">
                                <span className="text-zinc-800 font-medium">Val. Stoc (Magazin + Depozit)</span>
                                <div className="text-right">
                                    <p className="font-bold text-lg text-zinc-900">
                                        {formatPrice(data.stock?.value_ron || 0)} RON
                                    </p>
                                    <p className="text-sm text-blue-600">
                                        {formatPrice(data.stock?.value_eur || 0)} EUR
                                    </p>
                                </div>
                            </div>
                            
                            {/* Transport Fees Section */}
                            <div className="border-t border-zinc-200 pt-3 mt-3">
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <Truck size={16} className="text-orange-500" />
                                        <span className="text-zinc-700 font-medium">Taxe Transport (Luna curentă)</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="font-bold text-orange-600">
                                            {formatPrice(data.transport?.month_ron || 0)} RON
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={loadTransportHistory}
                                            className="text-xs text-blue-600 hover:text-blue-700 h-6 px-2"
                                        >
                                            Vezi istoric
                                        </Button>
                                    </div>
                                </div>
                                <p className="text-xs text-zinc-400 mt-1 ml-6">
                                    {data.transport?.count || 0} comenzi cu transport
                                </p>
                            </div>
                            
                            {/* TVA Summary Card */}
                            {tvaSummary && (
                                <div className="border-t border-zinc-200 pt-3 mt-3">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Receipt size={16} className="text-indigo-500" />
                                        <span className="text-zinc-700 font-medium">Sold TVA ({tvaSummary.month_name})</span>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                        <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                                            <p className="text-xs text-emerald-600 uppercase tracking-wide mb-1">TVA Deductibil</p>
                                            <p className="text-lg font-bold text-emerald-700">
                                                {formatPrice(tvaSummary.tva_deductibil || 0)} <span className="text-sm font-normal">RON</span>
                                            </p>
                                            <p className="text-xs text-emerald-500 mt-1">
                                                din {tvaSummary.details?.cheltuieli_count || 0} facturi
                                                {tvaSummary.details?.facturi_scutite > 0 && (
                                                    <span className="text-amber-600"> ({tvaSummary.details.facturi_scutite} scutite)</span>
                                                )}
                                            </p>
                                        </div>
                                        <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                                            <p className="text-xs text-red-600 uppercase tracking-wide mb-1">TVA Colectat</p>
                                            <p className="text-lg font-bold text-red-700">
                                                {formatPrice(tvaSummary.tva_colectat || 0)} <span className="text-sm font-normal">RON</span>
                                            </p>
                                            <p className="text-xs text-red-500 mt-1">
                                                din {tvaSummary.details?.vanzari_items || 0} articole
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div className={`p-3 rounded-lg border ${
                                        tvaSummary.status === 'de_recuperat' 
                                            ? 'bg-blue-50 border-blue-200' 
                                            : tvaSummary.status === 'de_plata'
                                                ? 'bg-orange-50 border-orange-200'
                                                : 'bg-zinc-50 border-zinc-200'
                                    }`}>
                                        <div className="flex justify-between items-center">
                                            <span className={`font-medium ${
                                                tvaSummary.status === 'de_recuperat' 
                                                    ? 'text-blue-700' 
                                                    : tvaSummary.status === 'de_plata'
                                                        ? 'text-orange-700'
                                                        : 'text-zinc-700'
                                            }`}>
                                                {tvaSummary.status === 'de_recuperat' 
                                                    ? 'TVA de Recuperat' 
                                                    : tvaSummary.status === 'de_plata'
                                                        ? 'TVA de Plată'
                                                        : 'Sold TVA'}
                                            </span>
                                            <span className={`text-xl font-bold ${
                                                tvaSummary.status === 'de_recuperat' 
                                                    ? 'text-blue-700' 
                                                    : tvaSummary.status === 'de_plata'
                                                        ? 'text-orange-700'
                                                        : 'text-zinc-700'
                                            }`}>
                                                {tvaSummary.status === 'de_recuperat' ? '+' : tvaSummary.status === 'de_plata' ? '-' : ''}
                                                {formatPrice(Math.abs(tvaSummary.sold_tva || 0))} RON
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Activity Counter Card */}
                    {activityCounter && (
                        <div className="mt-6 p-4 bg-gradient-to-br from-rose-50 to-rose-100 border border-rose-200 rounded-xl" data-testid="activity-counter-card">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <div className="w-10 h-10 rounded-lg bg-rose-500 flex items-center justify-center">
                                        <Activity size={20} className="text-white" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-rose-800">Contor Activitate</h3>
                                        <p className="text-xs text-rose-500">
                                            Resetat: {activityCounter.last_reset ? new Date(activityCounter.last_reset).toLocaleString('ro-RO') : '-'}
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={resetActivityCounter}
                                    disabled={resettingCounter}
                                    className="border-rose-300 text-rose-600 hover:bg-rose-100"
                                    data-testid="reset-counter-btn"
                                >
                                    <RotateCcw size={14} className={resettingCounter ? 'animate-spin' : ''} />
                                    <span className="ml-1">Reset</span>
                                </Button>
                            </div>
                            
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <div className="bg-white/60 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-rose-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {activityCounter.total_qty || 0}
                                    </p>
                                    <p className="text-xs text-rose-500 uppercase tracking-wide">Total Produse</p>
                                </div>
                                <div className="bg-white/60 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {activityCounter.orders_qty || 0}
                                    </p>
                                    <p className="text-xs text-amber-500 uppercase tracking-wide">Comenzi</p>
                                </div>
                                <div className="bg-white/60 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-teal-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {activityCounter.reservations_qty || 0}
                                    </p>
                                    <p className="text-xs text-teal-500 uppercase tracking-wide">Rezervări</p>
                                </div>
                                <div className="bg-white/60 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-cyan-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {activityCounter.virtual_orders_qty || 0}
                                    </p>
                                    <p className="text-xs text-cyan-500 uppercase tracking-wide">Virtuale</p>
                                </div>
                            </div>
                            
                            <div className="mt-3 pt-3 border-t border-rose-200 flex justify-between items-center">
                                <span className="text-sm text-rose-600">Valoare totală adăugată</span>
                                <span className="font-bold text-lg text-rose-700">
                                    {formatPrice(activityCounter.total_value_ron || 0)} RON
                                </span>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center py-8 text-zinc-500">
                    Nu s-au putut încărca datele
                </div>
            )}
            
            {/* Transport History Modal */}
            <Dialog open={showTransportModal} onOpenChange={(open) => { setShowTransportModal(open); if (!open) setExpandedMonth(null); }}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Truck size={20} className="text-orange-500" />
                            Istoric Taxe Transport
                        </DialogTitle>
                    </DialogHeader>
                    
                    {transportHistory && (
                        <div className="space-y-4 mt-4">
                            {/* Total */}
                            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                                <p className="text-sm text-orange-600">Total încasat din transport</p>
                                <p className="text-xl font-bold text-orange-700">
                                    {formatPrice(transportHistory.total_ron || 0)} RON
                                </p>
                            </div>
                            
                            {/* Monthly breakdown */}
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                                {transportHistory.months?.length > 0 ? (
                                    transportHistory.months.map((m, idx) => (
                                        <div key={`${m.year}-${m.month}`} className="border border-zinc-200 rounded-lg overflow-hidden">
                                            <div 
                                                className="flex justify-between items-center p-2 bg-zinc-50 hover:bg-zinc-100 cursor-pointer"
                                                onClick={() => setExpandedMonth(expandedMonth === idx ? null : idx)}
                                            >
                                                <span className="text-zinc-700 font-medium flex items-center gap-2">
                                                    <span className={`transition-transform ${expandedMonth === idx ? 'rotate-90' : ''}`}>▶</span>
                                                    {getMonthName(m.month)} {m.year}
                                                </span>
                                                <div className="text-right">
                                                    <span className="font-semibold text-zinc-900">
                                                        {formatPrice(m.value_ron)} RON
                                                    </span>
                                                    <span className="text-xs text-zinc-400 ml-2">
                                                        ({m.count} comenzi)
                                                    </span>
                                                </div>
                                            </div>
                                            {expandedMonth === idx && m.details && m.details.length > 0 && (
                                                <div className="bg-white border-t border-zinc-200 p-2 space-y-1">
                                                    {m.details.map((d) => (
                                                        <div key={d.customer_id || d.customer_name} className="flex justify-between items-center text-sm py-1 px-2 bg-zinc-50 rounded">
                                                            <span className="text-zinc-600 truncate max-w-[200px]">
                                                                {d.customer_name}
                                                            </span>
                                                            <span className="font-medium text-orange-600">
                                                                {formatPrice(d.value)} RON
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-zinc-500 text-center py-4">
                                        Nu există taxe de transport înregistrate
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Sales History Modal */}
            <Dialog open={showSalesModal} onOpenChange={(open) => { setShowSalesModal(open); if (!open) setExpandedSalesMonth(null); }}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <TrendingUp size={20} className="text-violet-500" />
                            Istoric Vânzări
                        </DialogTitle>
                    </DialogHeader>
                    
                    {salesHistory && (
                        <div className="space-y-4 mt-4">
                            {/* Monthly breakdown */}
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                {salesHistory.months?.length > 0 ? (
                                    salesHistory.months.map((m) => (
                                        <div key={`sales-${m.year}-${m.month}`} className="border border-zinc-200 rounded-lg overflow-hidden">
                                            <div 
                                                className="flex justify-between items-center p-3 bg-zinc-50 hover:bg-zinc-100 cursor-pointer"
                                                onClick={() => setExpandedSalesMonth(expandedSalesMonth === `${m.year}-${m.month}` ? null : `${m.year}-${m.month}`)}
                                            >
                                                <span className="text-zinc-700 font-medium flex items-center gap-2">
                                                    <span className={`transition-transform ${expandedSalesMonth === `${m.year}-${m.month}` ? 'rotate-90' : ''}`}>▶</span>
                                                    {getMonthName(m.month)} {m.year}
                                                </span>
                                                <div className="text-right">
                                                    <span className="font-bold text-violet-600">
                                                        {formatPrice(m.total_value_ron)} RON
                                                    </span>
                                                </div>
                                            </div>
                                            {expandedSalesMonth === `${m.year}-${m.month}` && (
                                                <div className="bg-white border-t border-zinc-200 p-3 space-y-2">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-zinc-500">Comenzi normale:</span>
                                                        <span className="font-medium text-emerald-600">
                                                            {formatPrice(m.orders_value_ron)} RON ({m.orders_count} articole)
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-zinc-500">Comenzi virtuale:</span>
                                                        <span className="font-medium text-cyan-600">
                                                            {formatPrice(m.virtual_value_ron)} RON ({m.virtual_count} comenzi)
                                                        </span>
                                                    </div>
                                                    {m.details && m.details.length > 0 && (
                                                        <div className="border-t border-zinc-100 pt-2 mt-2 space-y-1">
                                                            {m.details.slice(0, 10).map((d) => (
                                                                <div key={d.customer_id || d.customer_name} className="flex justify-between items-center text-sm py-1 px-2 bg-zinc-50 rounded">
                                                                    <span className="text-zinc-600 truncate max-w-[200px] flex items-center gap-1">
                                                                        {d.type === 'virtual' && <Package size={12} className="text-cyan-500" />}
                                                                        {d.customer_name}
                                                                    </span>
                                                                    <span className={`font-medium ${d.type === 'virtual' ? 'text-cyan-600' : 'text-violet-600'}`}>
                                                                        {formatPrice(d.value)} RON
                                                                    </span>
                                                                </div>
                                                            ))}
                                                            {m.details.length > 10 && (
                                                                <p className="text-xs text-zinc-400 text-center">
                                                                    și alte {m.details.length - 10} tranzacții...
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-zinc-500 text-center py-4">
                                        Nu există vânzări înregistrate
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AcasaV2;
